import { Box, useTheme } from '@mui/material';
import {
  CollectionOrderDirection,
  DefinitionForType,
  ResourceType,
  isErrorStatus,
  isLoadingStatus,
  useCollection,
} from 'json-api-hooks';
import React, { ReactNode, useContext } from 'react';

import ErrorCard from '../Error/ErrorCard';
import { LoadingCardContent } from '../Loading';
import Table, { Column } from '../Table';

import { CollectionContext } from './Collection';
import CollectionPagination from './CollectionPagination';

interface CollectionTableProps<T extends ResourceType> {
  columns: Column<T>[];
  onLoad?: () => ReactNode;
  onEmpty?: () => ReactNode;
  onRowClick?: (item: DefinitionForType<T>) => void;
  showHead?: boolean;
}

const CollectionTable = <T extends ResourceType>({
  columns,
  onEmpty,
  onLoad,
  onRowClick,
  showHead,
}: CollectionTableProps<T>) => {
  const theme = useTheme();
  const { collectionOpts, resourceType, updateCollection } = useContext(CollectionContext);
  const { order } = collectionOpts;
  const [items, { status }] = useCollection(resourceType, collectionOpts);

  const handleSortChange = (value: string, direction: CollectionOrderDirection | undefined) => {
    updateCollection({ order: direction ? { [value]: direction } : {} });
  };

  if (isLoadingStatus(status)) {
    return onLoad ? onLoad() : <LoadingCardContent />;
  }

  if (isErrorStatus(status)) {
    return <ErrorCard />;
  }

  if (items.length === 0 && onEmpty) {
    return (
      <Box component="span" sx={{ color: theme.palette.grey.midDark }}>
        {onEmpty()}
      </Box>
    );
  }

  return (
    <>
      <Table<T>
        columns={columns}
        rows={items as DefinitionForType<T>[]}
        showHead={showHead}
        sort={order}
        onRowClick={onRowClick}
        onSortChange={handleSortChange}
      />
      <CollectionPagination />
    </>
  );
};

export default CollectionTable;
